/**
 * Drawer - Slide-in Panel Component
 *
 * Side panel that slides in from left/right (typically for filters, menus).
 * Compound component pattern.
 *
 * @example
 * <Drawer.Root open={isOpen} onOpenChange={setIsOpen}>
 *   <Drawer.Overlay />
 *   <Drawer.Content side="left">
 *     <Drawer.Header>
 *       <Drawer.Title>Filters</Drawer.Title>
 *       <Drawer.Close />
 *     </Drawer.Header>
 *     <Drawer.Body>Content here</Drawer.Body>
 *   </Drawer.Content>
 * </Drawer.Root>
 */
import { createContext, useContext, useEffect, forwardRef } from "react";
import { X } from "lucide-react";
import { Box, Text } from "@/shared/ui/base";
import { cn } from "@/shared/lib/utils/cn";
import { useEscapeKey } from "@/shared/hooks";
import type { BaseComponentProps } from "@/shared/ui/base";

/**
 * Drawer Context
 */
interface DrawerContextValue {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

const DrawerContext = createContext<DrawerContextValue | null>(null);

function useDrawerContext() {
  const context = useContext(DrawerContext);
  if (!context) {
    throw new Error(
      "Drawer compound components must be used within Drawer.Root",
    );
  }
  return context;
}

/**
 * Drawer.Root - Root container with context
 */
interface DrawerRootProps extends BaseComponentProps {
  /** Drawer open state */
  open: boolean;
  /** Open state change handler */
  onOpenChange: (open: boolean) => void;
}

function DrawerRoot({ open, onOpenChange, children }: DrawerRootProps) {
  // Prevent body scroll when drawer is open
  useEffect(() => {
    if (open) {
      document.body.style.overflow = "hidden";
      return () => {
        document.body.style.overflow = "";
      };
    }
  }, [open]);

  if (!open) return null;

  return (
    <DrawerContext.Provider value={{ open, onOpenChange }}>
      <Box className="fixed inset-0 z-50">{children}</Box>
    </DrawerContext.Provider>
  );
}

/**
 * Drawer.Overlay - Background overlay
 */
const DrawerOverlay = forwardRef<HTMLDivElement, BaseComponentProps>(
  ({ className, ...props }, ref) => {
    const { onOpenChange } = useDrawerContext();

    return (
      <Box
        ref={ref}
        className={cn("absolute inset-0 bg-black/50", className)}
        onClick={() => onOpenChange(false)}
        {...props}
      />
    );
  },
);

DrawerOverlay.displayName = "Drawer.Overlay";

/**
 * Drawer.Content - Main drawer panel
 */
interface DrawerContentProps extends BaseComponentProps {
  /** Side to slide in from */
  side?: "left" | "right";
  /** Max width */
  size?: "sm" | "md" | "lg";
}

const DrawerContent = forwardRef<HTMLDivElement, DrawerContentProps>(
  ({ side = "right", size = "sm", className, children, ...props }, ref) => {
    const { onOpenChange } = useDrawerContext();

    // Close on Escape key
    useEscapeKey(() => onOpenChange(false));

    const sideClasses = {
      left: "left-0",
      right: "right-0",
    };

    const sizeClasses = {
      sm: "max-w-sm",
      md: "max-w-md",
      lg: "max-w-lg",
    };

    return (
      <Box
        ref={ref}
        className={cn(
          "absolute top-0 bottom-0 w-full bg-white shadow-xl overflow-y-auto",
          sideClasses[side],
          sizeClasses[size],
          className,
        )}
        {...props}
      >
        {children}
      </Box>
    );
  },
);

DrawerContent.displayName = "Drawer.Content";

/**
 * Drawer.Header - Header section
 */
const DrawerHeader = forwardRef<HTMLDivElement, BaseComponentProps>(
  ({ className, children, ...props }, ref) => {
    return (
      <Box
        ref={ref}
        className={cn(
          "sticky top-0 bg-white px-4 py-4 border-b border-gray-200 flex items-center justify-between z-10",
          className,
        )}
        {...props}
      >
        {children}
      </Box>
    );
  },
);

DrawerHeader.displayName = "Drawer.Header";

/**
 * Drawer.Title - Drawer title text
 */
const DrawerTitle = forwardRef<HTMLHeadingElement, BaseComponentProps>(
  ({ className, children, ...props }, ref) => {
    return (
      <Text
        as="h2"
        ref={ref}
        variant="h4"
        className={cn("font-bold text-gray-900", className)}
        {...props}
      >
        {children}
      </Text>
    );
  },
);

DrawerTitle.displayName = "Drawer.Title";

/**
 * Drawer.Close - Close button
 */
const DrawerClose = forwardRef<HTMLButtonElement, BaseComponentProps>(
  ({ className, ...props }, ref) => {
    const { onOpenChange } = useDrawerContext();

    return (
      <Box
        as="button"
        type="button"
        ref={ref}
        onClick={() => onOpenChange(false)}
        className={cn(
          "p-1.5 hover:bg-gray-100 rounded-lg transition-colors",
          className,
        )}
        aria-label="Close"
        {...props}
      >
        <X className="w-5 h-5 text-gray-500" />
      </Box>
    );
  },
);

DrawerClose.displayName = "Drawer.Close";

/**
 * Drawer.Body - Content body section
 */
const DrawerBody = forwardRef<HTMLDivElement, BaseComponentProps>(
  ({ className, children, ...props }, ref) => {
    return (
      <Box ref={ref} className={cn("p-4", className)} {...props}>
        {children}
      </Box>
    );
  },
);

DrawerBody.displayName = "Drawer.Body";

/**
 * Export Drawer compound component
 */
export const Drawer = {
  Root: DrawerRoot,
  Overlay: DrawerOverlay,
  Content: DrawerContent,
  Header: DrawerHeader,
  Title: DrawerTitle,
  Close: DrawerClose,
  Body: DrawerBody,
};
